import { useLayoutEffect, useMemo, useRef } from 'react';
import { CONFIG, isEntryEditable, type Entry, type Fixture, type Round } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { ArrowRight } from '../../components/ui/icons';
import { StakeLadder } from '../../components/play/cycling-ui';
import { cn } from '../../lib/cn';
import { stagePhaseTone, stageStatusPill } from '../play/stages-tab/helpers';
import { buildStageLadder } from '../play/stages-tab/buildStageLadder';
import { BackBar } from './BackBar';
import { CyclingSingleDuelFallback } from './CyclingSingleDuelFallback';

const PICKS_REQUIRED = CONFIG.CYCLING.DUELS_PER_STAGE;

/**
 * Cycling duel detail — full stake ladder with the tapped duel highlighted.
 * Falls back to a single-duel card when the player hasn't completed their ladder.
 */
export function CyclingDuelDetail({
  fixture,
  fixtures,
  entry,
  round,
  leagueName,
  onBack,
  onPicks,
  onBoard,
  embedded = false
}: {
  fixture: Fixture;
  fixtures: Fixture[];
  entry: Entry | null;
  round: Round | null;
  leagueName: string;
  onBack: () => void;
  onPicks: () => void;
  onBoard: () => void;
  /** Rendered inside a slide-up overlay — skips AppShell so the host owns scroll/surface. */
  embedded?: boolean;
}) {
  const ladderRef = useRef<HTMLDivElement>(null);
  const stageTone = stagePhaseTone(round?.status);
  const stageLive = stageTone === 'live' || stageTone === 'settling';
  const pickCount = entry?.duelPicks?.length ?? 0;
  const hasFullLadder = pickCount >= PICKS_REQUIRED;
  const now = Date.now();
  const canEnter = !!round && isEntryEditable(round, [], now);
  const stagePill = stageStatusPill(stageTone, {
    pickCount,
    picksRequired: PICKS_REQUIRED,
    isCurrentStage: true,
    entryEditable: canEnter
  });

  const ladder = useMemo(
    () => (hasFullLadder && entry ? buildStageLadder(entry, fixtures) : null),
    [hasFullLadder, entry, fixtures]
  );

  useLayoutEffect(() => {
    if (!ladder) return;
    const el = ladderRef.current?.querySelector('[data-highlighted="true"]');
    el?.scrollIntoView({ block: 'center' });
  }, [fixture.fixtureId, ladder]);

  const showBoard = !!entry && (round?.status === 'LOCKING' || round?.status === 'SETTLING' || round?.status === 'SETTLED');

  const body = (
    <>
      <BackBar onBack={onBack} eyebrow={`${leagueName} · ${round?.label ?? 'Stage'}`} tone="light" />

      <div className={cn(!embedded && 'mt-3', embedded && 'mt-1')}>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'rounded-[7px] px-2 py-[3px] text-[10px] font-extrabold uppercase tracking-[0.06em]',
              stagePill.className
            )}
          >
            {stagePill.label}
          </span>
          {fixture.status === 'LIVE' && (
            <span className="inline-flex items-center gap-1 rounded-[7px] bg-[rgba(227,168,43,0.18)] px-2 py-[3px] text-[10px] font-extrabold uppercase tracking-[0.06em] text-[#B5740A]">
              <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-[#EF4135]" />
              Stage live
            </span>
          )}
        </div>

        {ladder ? (
          <div ref={ladderRef} className="mt-3">
            <StakeLadder
              rungs={ladder.rungs}
              weights={ladder.weights}
              readOnly
              rungGrades={ladder.grades}
              stageLive={stageLive}
              highlightDuelId={fixture.fixtureId}
              showDetailPanel
              footerHint="Your full ladder for this stage"
              rankedCount={PICKS_REQUIRED}
              total={PICKS_REQUIRED}
            />
          </div>
        ) : (
          <CyclingSingleDuelFallback
            fixture={fixture}
            fixtures={fixtures}
            entry={entry}
            round={round}
            canEnter={canEnter}
            stageLive={stageLive}
            onPicks={onPicks}
          />
        )}

        {showBoard && (
          <button
            type="button"
            onClick={onBoard}
            className="tap mt-4 flex w-full items-center justify-center gap-2 rounded-[16px] border border-black/10 bg-white py-3.5 text-[13px] font-extrabold text-ink shadow-tile active:bg-fill/40"
          >
            Live division board
            <ArrowRight width={14} height={14} strokeWidth={2.6} className="text-ink3" />
          </button>
        )}
      </div>
    </>
  );

  if (embedded) return body;

  return (
    <AppShell refreshable={false}>
      <Content>{body}</Content>
    </AppShell>
  );
}
